import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getLanguages, createLanguage, deleteLanguage, updateLanguage } from "../../redux/languageSlice";
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  TextField,
  Button,
  CircularProgress,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const LanguageManagement = () => {
  const dispatch = useDispatch();
  const { fetchedLanguages: languages, loading, error } = useSelector((state) => state.languages);
  const [newLanguageName, setNewLanguageName] = useState("");
  const [newLanguageCode, setNewLanguageCode] = useState("");
  const [editingLanguageId, setEditingLanguageId] = useState(null);
  const [editedLanguageName, setEditedLanguageName] = useState("");

  const handleEditClick = (language) => {
    setEditingLanguageId(language._id);
    setEditedLanguageName(language.name);
  };

  const handleUpdateLanguage = () => {
    if (!editedLanguageName.trim()) return;
    dispatch(updateLanguage({ id: editingLanguageId, name: editedLanguageName }))
      .unwrap()
      .then(() => {
        toast.success("Language updated.");
        setEditingLanguageId(null);
        setEditedLanguageName("");
        dispatch(getLanguages());
      })
      .catch((err) => {
        const message =
          err?.response?.data?.message ||
          (typeof err === "string" ? err : err?.message) ||
          "Language update failed.";
        toast.error(message);
      });
  };

  useEffect(() => {
    dispatch(getLanguages());
  }, [dispatch]);

  const handleAddLanguage = () => {
    const trimmedName = newLanguageName.trim();
    const trimmedCode = newLanguageCode.trim();
    
    if (!trimmedName || !trimmedCode) {
      toast.error("Name and Code are required.");
      return;
    }

    const alreadyExists = languages?.some(
      (language) => language.name?.trim().toLowerCase() === trimmedName.toLowerCase() || 
                    language.code?.trim().toLowerCase() === trimmedCode.toLowerCase()
    );

    if (alreadyExists) {
      toast.error("Language already exists.");
      return;
    }

    dispatch(createLanguage({ name: newLanguageName, code: newLanguageCode }))
      .unwrap()
      .then(() => {
        toast.success("Language added.");
        setNewLanguageName("");
        setNewLanguageCode("");
        dispatch(getLanguages());
      })
      .catch((err) => {
        console.error("Language create error:", err);
        const message =
          err?.response?.data?.message ||
          (typeof err === "string" ? err : err?.message) ||
          "Language creation failed.";
        toast.error(message);
      });
  };

  useEffect(() => {
    if (error) {
      toast.error(typeof error === "string" ? error : "Language request failed.");
    }
  }, [error]);

  const handleDeleteLanguage = (id) => {
    if (window.confirm("Are you sure you want to delete this language?")) {
        dispatch(deleteLanguage(id))
        .unwrap()
        .then(() => {
            toast.success("Language deleted.");
            dispatch(getLanguages());
        })
        .catch((err) => {
            toast.error(err || "Language deletion failed.");
        });
    }
  };

  if (loading && !languages) {
    return (
      <Box display="flex" justifyContent="center" mt={5}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography
        variant="h5"
        gutterBottom
        sx={{ fontSize: { xs: "20px", md: "24px" }, lineHeight: 1.3 }}
      >
        Language Management
      </Typography>

      <Box display="flex" gap={2} mb={3} flexWrap="wrap">
        <TextField
          label="New language name"
          value={newLanguageName}
          onChange={(e) => setNewLanguageName(e.target.value)}
          size="small"
        />
        <TextField
          label="Code (e.g., en, tr)"
          value={newLanguageCode}
          onChange={(e) => setNewLanguageCode(e.target.value)}
          size="small"
          inputProps={{ maxLength: 5 }}
        />
        <Button variant="contained" onClick={handleAddLanguage} disabled={loading} type="button">
          {loading ? "Adding..." : "Add"}
        </Button>
      </Box>

      <List>
        {languages && languages.map((language) => (
          <ListItem key={language._id} divider>
            {editingLanguageId === language._id ? (
              <>
                <TextField
                  value={editedLanguageName}
                  onChange={(e) => setEditedLanguageName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleUpdateLanguage();
                  }}
                  size="small"
                  sx={{ mr: 2 }}
                />
                <Button
                  onClick={handleUpdateLanguage}
                  variant="contained"
                  color="primary"
                  size="small"
                  sx={{ ml: 1 }}
                >
                  Save
                </Button>
              </>
            ) : (
              <ListItemText 
                primary={language.name} 
                secondary={`Code: ${language.code || 'N/A'}`} 
              />
            )}
            <ListItemSecondaryAction>
              <IconButton edge="end" aria-label="edit" onClick={() => handleEditClick(language)} disabled={loading}>
                <EditIcon />
              </IconButton>
              <IconButton edge="end" aria-label="delete" onClick={() => handleDeleteLanguage(language._id)} disabled={loading}>
                <DeleteIcon />
              </IconButton>
            </ListItemSecondaryAction>
          </ListItem>
        ))}
      </List>
      <ToastContainer position="top-right" autoClose={3000} />
    </Box>
  );
};

export default LanguageManagement;
